import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import './Charts.css'

/**
 * PieChart Component
 *
 * A simple SVG-based pie chart with hover effects and legend.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array
 * @param {string} props.labelKey - Key for labels in data
 * @param {string} props.valueKey - Key for values in data
 * @param {string} props.title - Chart title
 * @param {boolean} props.showLegend - Show legend
 * @param {boolean} props.showLabels - Show labels on chart
 * @param {boolean} props.showPercentages - Show percentages in labels
 * @param {Array} props.colors - Custom color palette
 * @param {Function} props.formatValue - Value formatter function
 * @param {number} props.size - Chart size in pixels
 */
const PieChart = ({
	data = [],
	labelKey = 'label',
	valueKey = 'value',
	title,
	showLegend = true,
	showLabels = false,
	showPercentages = true,
	colors = defaultColors,
	formatValue = (v) => v,
	size = 280,
}) => {
	const [hoveredIndex, setHoveredIndex] = useState(null)

	// Calculate total
	const total = useMemo(() => {
		return data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)
	}, [data, valueKey])

	// Calculate slices
	const slices = useMemo(() => {
		if (!total) return []

		let currentAngle = 0
		return data.map((item, index) => {
			const value = item[valueKey] || 0
			const percentage = total ? (value / total) * 100 : 0
			const angle = (percentage / 100) * 360

			const slice = {
				...item,
				value,
				percentage,
				startAngle: currentAngle,
				endAngle: currentAngle + angle,
				color: item.color || colors[index % colors.length],
			}

			currentAngle += angle
			return slice
		})
	}, [data, valueKey, total, colors])

	// Generate path for slice
	const getSlicePath = (slice, isHovered) => {
		const centerX = size / 2
		const centerY = size / 2
		const radius = (size / 2 - 10) * (isHovered ? 1.05 : 1)
		const innerRadius = 0

		const startRad = ((slice.startAngle - 90) * Math.PI) / 180
		const endRad = ((slice.endAngle - 90) * Math.PI) / 180

		const x1 = centerX + radius * Math.cos(startRad)
		const y1 = centerY + radius * Math.sin(startRad)
		const x2 = centerX + radius * Math.cos(endRad)
		const y2 = centerY + radius * Math.sin(endRad)

		const largeArc = slice.endAngle - slice.startAngle > 180 ? 1 : 0

		// For full circle
		if (slice.percentage >= 99.9) {
			return `M ${centerX},${centerY - radius} A ${radius},${radius} 0 1 1 ${centerX - 0.001},${centerY - radius} Z`
		}

		return `M ${centerX},${centerY} L ${x1},${y1} A ${radius},${radius} 0 ${largeArc} 1 ${x2},${y2} Z`
	}

	// Get label position
	const getLabelPosition = (slice) => {
		const centerX = size / 2
		const centerY = size / 2
		const radius = size / 2 - 40
		const midAngle = (slice.startAngle + slice.endAngle) / 2 - 90
		const rad = (midAngle * Math.PI) / 180

		return {
			x: centerX + radius * Math.cos(rad),
			y: centerY + radius * Math.sin(rad),
		}
	}

	if (!data.length || !total) {
		return (
			<div className="chart chart--empty">
				<p>No data available</p>
			</div>
		)
	}

	return (
		<div className="chart pie-chart">
			{title && <h4 className="chart__title">{title}</h4>}

			<div className="pie-chart__container">
				<svg
					width={size}
					height={size}
					viewBox={`0 0 ${size} ${size}`}
					className="pie-chart__svg"
				>
					{slices.map((slice, index) => (
						<g key={index}>
							<path
								d={getSlicePath(slice, hoveredIndex === index)}
								fill={slice.color}
								stroke="white"
								strokeWidth="2"
								className={`pie-chart__slice ${hoveredIndex === index ? 'pie-chart__slice--hovered' : ''}`}
								onMouseEnter={() => setHoveredIndex(index)}
								onMouseLeave={() => setHoveredIndex(null)}
							>
								<title>
									{slice[labelKey]}: {formatValue(slice.value)} ({slice.percentage.toFixed(1)}%)
								</title>
							</path>

							{showLabels && slice.percentage > 5 && (
								<text
									x={getLabelPosition(slice).x}
									y={getLabelPosition(slice).y}
									textAnchor="middle"
									dominantBaseline="middle"
									fill="white"
									fontSize="12"
									fontWeight="600"
									className="pie-chart__label"
								>
									{showPercentages ? `${slice.percentage.toFixed(0)}%` : formatValue(slice.value)}
								</text>
							)}
						</g>
					))}
				</svg>

				{showLegend && (
					<div className="pie-chart__legend">
						{slices.map((slice, index) => (
							<div
								key={index}
								className={`pie-chart__legend-item ${hoveredIndex === index ? 'pie-chart__legend-item--active' : ''}`}
								onMouseEnter={() => setHoveredIndex(index)}
								onMouseLeave={() => setHoveredIndex(null)}
							>
								<span
									className="pie-chart__legend-color"
									style={{ backgroundColor: slice.color }}
								/>
								<span className="pie-chart__legend-label">{slice[labelKey]}</span>
								<span className="pie-chart__legend-value">
									{formatValue(slice.value)}
									{showPercentages && ` (${slice.percentage.toFixed(1)}%)`}
								</span>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

// Default colors
const defaultColors = [
	'#3b82f6', // blue
	'#10b981', // green
	'#f59e0b', // amber
	'#ef4444', // red
	'#8b5cf6', // violet
	'#ec4899', // pink
	'#06b6d4', // cyan
	'#84cc16', // lime
]

PieChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	labelKey: PropTypes.string,
	valueKey: PropTypes.string,
	title: PropTypes.string,
	showLegend: PropTypes.bool,
	showLabels: PropTypes.bool,
	showPercentages: PropTypes.bool,
	colors: PropTypes.arrayOf(PropTypes.string),
	formatValue: PropTypes.func,
	size: PropTypes.number,
}

export default PieChart
